import React, { useState } from "react";
import { TextField } from "@mui/material";
import InfoTooltip from "./InfoTooltip";
import { useViewMode } from "../contexts/ViewModeContext";

const PositiveIntegerInput = ({
  label,
  maxValue,
  value,
  onChange,
  mandatory,
  instruction,
}) => {
  const [error, setError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const {isViewMode} = useViewMode();

  const handleChange = (event) => {
    const newValue = event.target.value;

    // If user types "e", "+", or "-" anywhere in the input,
    // immediately clear the field and set an error.
    if (/[e+\-]/.test(newValue)) {
      setError(true);
      onChange("");
      return;
    }

    // Only allow digits.
    if (/^\d*$/.test(newValue)) {
      // If the user clears the field, just update and let onBlur handle required logic.
      if (newValue === "") {
        onChange("");
        setError(false);
        return;
      }

      // Parse and validate the new numeric value.
      const intValue = parseInt(newValue, 10);
      // Must be ≥ 1 (or up to maxValue if provided).
      if (intValue < 1 || (maxValue && intValue > maxValue)) {
        setError(true);
        return;
      }
      setError(false);
      onChange(intValue);
    }
  };

  const handleBlur = (event) => {
    const inputValue = event.target.value.trim();

    // If there's input, validate it.
    if (inputValue !== "") {
      if (
        /[e+\-]/.test(inputValue) ||
        !/^\d+$/.test(inputValue) ||
        parseInt(inputValue, 10) < 1 ||
        (maxValue && parseInt(inputValue, 10) > maxValue)
      ) {
        setError(true);
        onChange("");
      } else {
        setError(false);
      }
    } else {
      // If it's empty, only show error if mandatory.
      if (mandatory) {
        setError(true);
        onChange("");
      } else {
        setError(false);
      }
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="annotation-form-field-container">
      {/* Standardized label */}
      <div className="annotation-form-field-label">
        <span className={`label-text ${mandatory ? 'mandatory' : ''}`}>
          {label}
        </span>
        {mandatory && <span className="required-asterisk">*</span>}
        {instruction && <InfoTooltip instruction={instruction} />}
      </div>

      {/* Input field */}
      <div className="annotation-form-field-input">
        <TextField
          type="text"  // <-- text instead of number
          value={value || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          size="small"
          variant="outlined"
          error={error}
          helperText={
            error
              ? maxValue
                ? `Must be a positive integer from 1 to ${maxValue}`
                : "Must be a positive integer (≥ 1)"
              : ""
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          disabled={isViewMode}
          sx={{ mt: 0 }}
        />
      </div>
    </div>
  );
};

export default PositiveIntegerInput;
